import {
  get,
  has,
  isNil,
  sortBy,
  isEmpty,
  cloneDeep,
  find,
} from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { setMessage } from '../../../../common/components/Alert/actions';
import { ERROR_TYPE_VALIDATION } from '../../../../common/constants/errorTypes';
import PlazaResource from '../../../../common/resources/Plaza';
import TLM from '../../../../common/resources/TLM';

import APIClient from '../../../../common/services/APIClient';
import { DEFAULT_PLAZA_TELEMETRY_TIMEOUT, setPlazaTopologyNodes } from '../../actions';
import { convertChargerPhaseValues, convertSessionEnergyValue } from '../../helper';
import { getExternalSources, getNodeList } from '../../selectors';
import { selectMeterWiring } from '../../../../common/components/appConfig/selector';

import {
  CHARGER_NODE_TYPE,
  CHARGERS_LATEST_METRICS,
  MEASUREMENT_POINT_NODE_TYPE,
  METER_LATEST_METRICS,
  multipleMeters,
  PARAM_NAMES,
  ROOT_NODE_TYPE,
} from '../constants';
import { getDemoData, makeDemoSourceGenerator } from '../Demo';
import { detectDemoMode } from '../Demo/helper';
import { ACTION_TYPES } from './types';

let requestController = null;

export const initializePlazaTopologyChart = (list, hierarchy, externalSources) => ({
  type: ACTION_TYPES.INIT_EDITOR,
  list,
  hierarchy,
  externalSources,
});

export const resetToInitialState = () => ({
  type: ACTION_TYPES.RESET_TO_INITIAL_STATE,
});

export const toggleNewPlaza = (isNew) => ({
  type: ACTION_TYPES.NEW_PLAZA,
  isNew,
});

export const toggleFetching = (isFetching, error = null) => ({
  type: ACTION_TYPES.TOGGLE_FETCHING,
  isFetching,
  error,
});

export const setPlazaId = (plazaId) => ({
  type: ACTION_TYPES.SET_PLAZA_ID,
  plazaId,
});

export const updateDynamicData = (data) => ({
  type: ACTION_TYPES.UPDATE_DYNAMIC_DATA,
  data,
});

export const setNodeList = (list) => ({
  type: ACTION_TYPES.SET_NODE_LIST,
  payload: { list },
});

export const setExternalSources = (sources) => ({
  type: ACTION_TYPES.SET_EXTERNAL_SOURCES,
  payload: { sources },
});

export const setRegularAndSourceNodes = (regular, sources) => ({
  type: ACTION_TYPES.SET_REGULAR_AND_EXTERNAL_SOURCES,
  payload: { regular, sources },
});

export const setEditableNodeId = (nodeId) => ({
  type: ACTION_TYPES.SET_EDITABLE_NODE_ID,
  payload: nodeId,
});

export const updateLocalTopology = (hierarchy) => ({
  type: ACTION_TYPES.UPDATE_TOPOLOGY,
  payload: { hierarchy },
});

export const updateLocalNodesList = (nodeList) => ({
  type: ACTION_TYPES.UPDATE_NODES_LIST,
  payload: { nodeList },
});

export const createLocalNode = (node) => ({
  type: ACTION_TYPES.CREATE_NODE,
  payload: { node },
});

export const createLocalResourceNode = (node) => ({
  type: ACTION_TYPES.CREATE_RESOURCE_NODE,
  payload: { node },
});

export const updateLocalNode = (id, props) => ({
  type: ACTION_TYPES.UPDATE_NODE,
  payload: { id, props },
});

export const updateLocalResourceNode = (id, props) => ({
  type: ACTION_TYPES.UPDATE_RESOURCE_NODE,
  payload: { id, props },
});

export const deleteLocalNode = (nodeId) => ({
  type: ACTION_TYPES.DELETE_NODE,
  payload: { nodeId },
});

export const deleteLocalResourceNode = (nodeId) => ({
  type: ACTION_TYPES.DELETE_RESOURCE_NODE,
  payload: { nodeId },
});

export const addToRequestQueue = (requestId, requestType, previousState = {}) => ({
  type: ACTION_TYPES.ADD_TO_REQUEST_QUEUE,
  payload: {
    requestId,
    requestType,
    previousState,
  },
});

export const removeFromRequestQueue = (requestId) => ({
  type: ACTION_TYPES.REMOVE_FROM_REQUEST_QUEUE,
  payload: { requestId },
});

export const setPlazaSettings = (settings) => ({
  type: ACTION_TYPES.SET_PLAZA_SETTINGS,
  payload: settings,
});

export const setNodeErrors = (errors) => ({
  type: ACTION_TYPES.SET_NODE_ERRORS,
  payload: errors,
});

export const setNodeTopologyErrors = (errors) => ({
  type: ACTION_TYPES.SET_NODE_TOPOLOGY_ERRORS,
  payload: errors,
});

export const setPlazaErrors = (errors) => ({
  type: ACTION_TYPES.SET_PLAZA_ERRORS,
  payload: errors,
});

export const setPlazaPatching = (isPatching) => ({
  type: ACTION_TYPES.SET_PLAZA_PATCHING,
  payload: isPatching,
});

export const setUpdatedStatus = (payload) => ({
  type: ACTION_TYPES.SET_UPDATED_STATUS,
  payload,
});

export const setChargersByAddress = (hierarchy, nodes) => ({
  type: ACTION_TYPES.SET_CHARGERS_BY_ADDRESS,
  payload: { hierarchy, nodes },
});

export const resetChargersByAddress = () => ({
  type: ACTION_TYPES.RESET_CHARGERS_BY_ADDRESS,
});

export const getUpdatedStatus = (plazaId) => (dispatch) => {
  if (requestController !== null && requestController.abort) {
    requestController.abort();
    requestController = null;
  }

  requestController = new AbortController();

  PlazaResource.getUpdatedStatus({ plazaId }, { signal: requestController.signal })
    .then(({ data }) => {
      const lastUpdated = get(data, 'lastUpdateInfo');

      dispatch(setUpdatedStatus(lastUpdated || null));
    })
    .catch(() => {
      dispatch(setUpdatedStatus(null));
    });
};

export const setEditorEditable = (isEditable) => ({
  type: ACTION_TYPES.SET_EDITOR_EDITABLE,
  payload: isEditable,
});

const handlePlazaValidationError = (error) => (dispatch) => {
  const nodeErrors = get(error, 'response.data.nodeValidationErrors');
  const plazaErrors = get(error, 'response.data.plazaValidationErrors');
  const nodeTopologyErrors = get(error, 'response.data.nodeTopologyValidationErrors');

  if (plazaErrors) {
    dispatch(setPlazaErrors(plazaErrors));
  }
  if (nodeErrors && nodeErrors.length > 0) {
    dispatch(setNodeErrors(nodeErrors));
  }
  if (nodeTopologyErrors && nodeTopologyErrors.length > 0) {
    dispatch(setNodeTopologyErrors(nodeTopologyErrors));
  }
  if (!nodeErrors && !plazaErrors) {
    if (get(error, 'response.status', null) === 423) {
      dispatch(setEditorEditable(false));
    }
    dispatch(setMessage({ message: 'Something went wrong, we got unexpected error' }));
  }
};

const findChildren = (item, findId) => {
  let foundChildren = [];
  const { id, children } = item;

  if (id === findId) {
    foundChildren = children;
  } else {
    if (children && children.length) {
      children.forEach(
        (child) => {
          const found = findChildren(child, findId);

          if (found && found.length) {
            foundChildren = found;
          }
        },
      );
    }
  }

  return foundChildren;
};

const normalizeNodes = (nodes, hierarchy) =>
  Object.keys(nodes).map((key) => {
    const node = nodes[key];
    const schedules = get(node, 'staticSection.schedules');
    const topologyNodeType = get(node, 'topologyNodeType');

    if (schedules && topologyNodeType !== ROOT_NODE_TYPE) {
      // Normalize schedule
      node.schedules = schedules.map((schedule) =>
        schedule.items.forEach(
          (item) => {
            item.to = item.to.toString().substr(-1) === '9' && item.to !== 86399 ? item.to + 1 : item.to;
          },
        ));
    }

    if (!has(node, 'children')) {
      node.children = findChildren(hierarchy, node.id);
    }

    return node;
  });

export const processFullPlazaResponse = (resp) => {
  const hierarchy = get(resp, 'data.plazaTopology');
  const nodeList = normalizeNodes(get(resp, 'data.topologyNodes'), hierarchy);
  let externalSources = get(resp, 'data.externalSources', {});

  externalSources = sortBy(Object.keys(externalSources).map(
    (key) => externalSources[key],
), 'staticSection.sourceOrdinalNumber');

  return { hierarchy, nodeList, externalSources };
};

export const patchPlaza = (plazaId, data) => (dispatch) =>
  new Promise((resolve, reject) => {
    dispatch(setPlazaPatching(true));
    PlazaResource.structureUpdate({ plazaId }, data)
      .then((response) => {
        const { hierarchy, nodeList, externalSources } = processFullPlazaResponse(response);

      dispatch(initializePlazaTopologyChart(nodeList, hierarchy, externalSources));
      dispatch(setPlazaTopologyNodes(get(response, 'data.topologyNodes')));
      dispatch(setPlazaPatching(false));
      dispatch(getUpdatedStatus(plazaId));
      dispatch(resetChargersByAddress());
      resolve();
    })
    .catch((err) => {
      dispatch(handlePlazaValidationError(err));
      dispatch(setPlazaPatching(false));
      reject(err);
    });
});

export const createRemoteNode = (nodeData, hierarchy, plazaId) => (dispatch) => {
  const requestId = uuidv4();
  const requestType = ACTION_TYPES.CREATE_NODE;

  dispatch(addToRequestQueue(requestId, requestType));

  return APIClient.post(`/plazas/${plazaId}/nodes`, { plazaTopology: hierarchy, topologyNode: nodeData })
    .then((resp) => {
      const { hierarchy, nodeList, externalSources } = processFullPlazaResponse(resp);

      dispatch(initializePlazaTopologyChart(nodeList, hierarchy, externalSources));
      dispatch(toggleNewPlaza(false));
    })
    .catch((error) => {
      dispatch(toggleNewPlaza(false));
      throw error;
    })
    .finally(() => dispatch(removeFromRequestQueue(requestId)));
};

export const updateRemoteNode =
  (nodeId, plazaId, presetNode = null, applyServerUpdate = false) =>
  (dispatch, getState) =>
    new Promise((resolve, reject) => {
      const state = getState();
      let node = presetNode;

      if (!node) {
        node =
          getNodeList(state).find((item) => item.id === nodeId) ||
          getExternalSources(state).find((item) => item.id === nodeId);
      }

        const requestId = uuidv4();
        const requestType = ACTION_TYPES.UPDATE_NODE;

      dispatch(addToRequestQueue(requestId, requestType, node));

      APIClient.put(`/plazas/${plazaId}/nodes/${nodeId}`, { topologyNode: node })
        .then((response) => {
          if (applyServerUpdate) {
            const nodes = get(response, 'data.topologyNodes');
            const sources = get(response, 'data.externalSources');

            dispatch(setNodeList(Object.keys(nodes).map((key) => nodes[key])));
            dispatch(setExternalSources(Object.keys(sources).map((key) => sources[key])));
          }
          resolve(response);
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => dispatch(removeFromRequestQueue(requestId)));
    });

export const handleNodeError = (error) => (dispatch) => {
  const errorType = get(error, 'response.data.error_type');
  const errors = get(error, 'response.data.errors');

  if (errorType === ERROR_TYPE_VALIDATION) {
    const errorFields = errors.reduce((fields, error) => {
      if (error.field) {
        fields[error.field.replace('nodeName', 'nodeTitle')] = error.message;
      } else {
        fields._error = error.message;
      }

      return fields;
    }, {});

    dispatch(setMessage({ message: errorFields }));
  } else {
    dispatch(setMessage({ message: 'Something went wrong, we got unexpected error' }));
  }
};

export const deleteRemoteNode = (nodeId, plazaId, appendResponse = false) => (dispatch) => {
  const requestId = uuidv4();
  const requestType = ACTION_TYPES.REMOVE_NODE;

    dispatch(addToRequestQueue(requestId, requestType));

    APIClient.delete(`/plazas/${plazaId}/nodes/${nodeId}`)
      .then((resp) => {
        if (appendResponse) {
          const { hierarchy, nodeList, externalSources } = processFullPlazaResponse(resp);

          dispatch(initializePlazaTopologyChart(nodeList, hierarchy, externalSources));
          dispatch(toggleNewPlaza(false));
        }
      })
      .catch((err) => dispatch(handleNodeError(err)))
      .finally(() => dispatch(removeFromRequestQueue(requestId)));
  };

export const updateRemoteTopology = (hierarchy, plazaId, nodeId = null, applyResponse = false) => (dispatch) => {
  const requestId = uuidv4();
  const requestType = ACTION_TYPES.UPDATE_TOPOLOGY;

  dispatch(addToRequestQueue(requestId, requestType));

  APIClient.put(
    `/plazas/${plazaId}/topology`,
    {
      'nodeId': nodeId,
      'plazaTopology': hierarchy,
    },
  ).then(
    (resp) => {
      if (applyResponse) {
        dispatch(updateLocalTopology(resp.data.plazaTopology));
      }
      const { nodeList } = processFullPlazaResponse(resp);

      dispatch(updateLocalNodesList(nodeList));
    },
  )
    .catch((err) => dispatch(handleNodeError(err)))
    .finally(() => dispatch(removeFromRequestQueue(requestId)));
};

const getTelemetryActualData = (telemetryData, prop, telemetryTimeout = DEFAULT_PLAZA_TELEMETRY_TIMEOUT) => {
  const telemetryValue = get(telemetryData, `${prop}.value`);
  const telemetryTimestamp = get(telemetryData, `${prop}.ts`);

  if (telemetryValue === null || !telemetryTimestamp) {
    return null;
  }

  const currentTimestamp = moment.utc().valueOf();
  const telemetryTimestampConverted =
    telemetryTimestamp.toString().length === 10 ? telemetryTimestamp * 1000 : telemetryTimestamp;

  return telemetryTimestampConverted < currentTimestamp - telemetryTimeout ? null : telemetryValue;
};

const mappedPhasesRotation = {
  1: 'PHASE_A',
  2: 'PHASE_B',
  3: 'PHASE_C',
  PHASE_A: 1,
  PHASE_B: 2,
  PHASE_C: 3,
};

const getRotatedMetric = (metric, phaseKey, absoluteRotation) => {
  metric = metric.toUpperCase();

  const index = absoluteRotation.indexOf(`${mappedPhasesRotation[phaseKey]}`) + 1;
  const newCode = mappedPhasesRotation[index];

  if (isNil(newCode)) {
    return;
  }

  return metric.replace(phaseKey, mappedPhasesRotation[index]);
};

export const getMetricByRotation = (metric, phases) => {
  const absoluteRotation = Object.values(phases);

  metric = metric.toUpperCase();

  if (metric.includes(mappedPhasesRotation[1])) {
    return getRotatedMetric(metric, mappedPhasesRotation[1], absoluteRotation);
  }
  if (metric.includes(mappedPhasesRotation[2])) {
    return getRotatedMetric(metric, mappedPhasesRotation[2], absoluteRotation);
  }
  if (metric.includes(mappedPhasesRotation[3])) {
    return getRotatedMetric(metric, mappedPhasesRotation[3], absoluteRotation);
  }

  return metric;
};

// as we consider that there is ONLY 1 option of meterRotation then we can do not use exact meterWiring object from the config
export const getMetricByMeterRotation = (metric) => {
  // TODO implement more comprehensive solution
  if (metric.includes(mappedPhasesRotation[1])) {
    return metric.replace(mappedPhasesRotation[1], mappedPhasesRotation[3]);
  }

  if (metric.includes(mappedPhasesRotation[3])) {
    return metric.replace(mappedPhasesRotation[3], mappedPhasesRotation[1]);
  }

  return metric;
};

const setTelemetryActualData = (nodes, nodeId, data, nodeName, telemetryTimeout, phases, meterWiring = null, meterModel = false) => {
  const meterModelAdd = meterModel ? `:${meterModel}` : ''

  for (const metric in CHARGERS_LATEST_METRICS) {
    if (metric === multipleMeters.SESSION_ENERGY_TOTAL_WS) {
      nodes[nodeId][PARAM_NAMES[metric]] = convertSessionEnergyValue(
        getTelemetryActualData(data, `${nodeName}.${metric}${meterModelAdd}`, telemetryTimeout),
      );
    } else {
      if (phases) {
        let metricByRotation = getMetricByRotation(metric, phases);

        if (metricByRotation) {

          if (meterWiring) {
            metricByRotation = getMetricByMeterRotation(metricByRotation);
          }

          nodes[nodeId][PARAM_NAMES[metric]] = convertChargerPhaseValues(
            getTelemetryActualData(data, `${nodeName}.${metricByRotation}${meterModelAdd}`, telemetryTimeout),
          );

        }
      } else {
        nodes[nodeId][PARAM_NAMES[metric]] = convertChargerPhaseValues(
          getTelemetryActualData(data, `${nodeName}.${metric}${meterModelAdd}`, telemetryTimeout),
        );
      }
    }
  }
};

// this changes are for Monitor tree and table views
const updateChargersTelemetry = async ({ chargers, chargerUuids, nodes, telemetryTimeout, dispatch, getState }) => {
  if (!chargerUuids.length) {
    return;
  }

  try {
    const response = await TLM.getSnapshot({
      chargerUuids,
      metrics: Object.keys(CHARGERS_LATEST_METRICS),
    });
    const data = await response.data;

    for (const nodeId in data) {
      const node = find(chargers, (chargerNode) => chargerNode.chargerId === nodeId);

      if (node) {
        if (node.online) {
          const meterWiring = selectMeterWiring(getState());

          setTelemetryActualData(nodes, node.nodeId, data, nodeId, telemetryTimeout, node.phases, meterWiring);
        } else {
          nodes[node.nodeId].chargingLoadRequested = null;
          nodes[node.nodeId].dischargingLoadRequested = null;
          nodes[node.nodeId].chargingPowerRequested = null;
          nodes[node.nodeId].dischargingPowerRequested = null;
          nodes[node.nodeId].chargingPowerLimit = null;
          nodes[node.nodeId].dischargingPowerLimit = null;
          nodes[node.nodeId].chargingLoadLimit = null;
          nodes[node.nodeId].dischargingLoadLimit = null;
          nodes[node.nodeId].sessionType = null;
          nodes[node.nodeId].presentSoc = null;
        }
      }
    }
  } catch (error) {
    console.error(error);
  } finally {
    dispatch(updateDynamicData(cloneDeep(nodes)));
  }
};

const updateMeasurementsTelemetry = async ({ measurements, measurementIds, nodes, telemetryTimeout, dispatch, meterModel = null }) => {
  if (!measurementIds.length) {
    return;
  }

  const meterModelAdd = meterModel ? `:${meterModel}` : ''

  try {
    const response = await TLM.getSnapshot({
      chargerUuids: measurementIds,
      metrics: METER_LATEST_METRICS.map(metric => meterModel ? `${metric}${meterModelAdd}` : metric),
    });
    const data = await response.data;

    for (const nodeId in data) {
      const meter = find(measurements, (measure) => measure.chargerId.localeCompare(nodeId, undefined, { sensitivity: 'base' }) === 0);

      if (meter) {
        setTelemetryActualData(nodes, meter.nodeId, data, nodeId, telemetryTimeout, meter.phases, null, meterModel);
      }
    }
  } catch (error) {
    console.error(error);
  } finally {
    dispatch(updateDynamicData(cloneDeep(nodes)));
  }
};

const sourceGenerator = makeDemoSourceGenerator();

export const loadDynamicData = (plazaId, plazaData, meterModel, telemetryTimeout) => (dispatch, getState) => {
  // TESTS:
  if (detectDemoMode()) {
    dispatch(updateDynamicData(sourceGenerator.next().value));

    return;
  }
  // TESTS ENDS

  APIClient.get(`/plazas/${plazaId}/monitoring-nodes-info/node-info`)
    .then((resp) => {
      const nodes = get(resp, 'data.dynamicSection');

      const chargers = [];
      const measurements = [];

      for (const nodeId in nodes) {
        const currentNodeData = nodes[nodeId];
        const nodeType = get(currentNodeData, 'nodeType', null);

        if (nodeType === MEASUREMENT_POINT_NODE_TYPE) {
          const externalId = get(plazaData, `topologyNodes.${nodeId}.staticSection.externalId`, null);
          const phases = get(plazaData, `topologyNodes.${nodeId}.staticSection.phases.absoluteRotation`, null);

          if (externalId) {
            measurements.push({
              nodeId,
              chargerId: externalId,
              phases,
            });
          }
        } else if (nodeType === CHARGER_NODE_TYPE) {
          const chargerId = get(plazaData, `topologyNodes.${nodeId}.staticSection.chargerUuid`, null);
          const phases = get(plazaData, `topologyNodes.${nodeId}.staticSection.phases.absoluteRotation`, null);

          if (chargerId) {
            chargers.push({
              nodeId,
              chargerId,
              online: get(nodes, `${nodeId}.online`, false),
              phases,
            });
          }
        }
      }

      const chargerUuids = chargers.map((charger) => charger.chargerId);
      const measurementIds = measurements.map((node) => node.chargerId);

      const chargerPromise = updateChargersTelemetry({
        chargers,
        chargerUuids,
        nodes,
        telemetryTimeout,
        dispatch,
        getState,
      });
      const measurementsPromise = updateMeasurementsTelemetry({
        measurements,
        measurementIds,
        nodes,
        telemetryTimeout,
        dispatch,
        meterModel,
      });

      if (!chargerUuids.length && !measurementIds.length) {
        dispatch(updateDynamicData(cloneDeep(nodes)));
      }

      Promise.all([chargerPromise, measurementsPromise]);
    })
    .catch((error) => {
      dispatch(updateDynamicData({}));
      console.error(error);
    });
};

const demoData = getDemoData();

const handleError = (error, dispatch) => {
  if (get(error, 'response.status') === 500) {
    dispatch(
      toggleFetching(
        false,
        'Something went wrong while performing your request. Please contact your site administrator.',
      ),
    );
  }
  dispatch(toggleFetching(false));
  console.error(error);
};

export const loadPlazaSettings = (plazaId) => (dispatch) => {
  APIClient.get(`/plazas/${plazaId}/settings`)
    .then(({ data }) => {
      dispatch(getUpdatedStatus(plazaId));
      dispatch(setPlazaSettings(data));
    })
    .catch((error) => {
      handleError(error, dispatch);
    });
};

export const loadMonitoringPlaza = (plazaId) => (dispatch) => {
  // TEST
  if (detectDemoMode()) {
    dispatch(toggleNewPlaza(false));
    dispatch(initializePlazaTopologyChart(demoData.nodeList, demoData.topology, demoData.externalSources));
    dispatch(toggleFetching(false));

    return;
  }
  // TEST END
  dispatch(toggleFetching(true));
  dispatch(initializePlazaTopologyChart([], {}, []));
  APIClient.get(`/plazas/${plazaId}`)
    .then((resp) => {
      const plazaId = get(resp, 'data.plazaUuid');

      dispatch(setPlazaId(plazaId));
      const { hierarchy, nodeList, externalSources } = processFullPlazaResponse(resp);

      if (isEmpty(nodeList) && (hierarchy === null || hierarchy.id === null)) {
        dispatch(toggleNewPlaza(true));
        dispatch(toggleFetching(false));

        return;
      }
      dispatch(toggleNewPlaza(false));

      dispatch(initializePlazaTopologyChart(nodeList, hierarchy, externalSources));
      dispatch(toggleFetching(false));
    })
    .catch((error) => {
      dispatch(toggleFetching(false));
      handleError(error, dispatch);
    });
};

export const setPlazaEditorLockInterval = (lockInterval) => ({
  type: ACTION_TYPES.SET_LOCK_INTERVAL,
  payload: lockInterval,
});

export const clearPlazaEditorLockInterval = () => ({
  type: ACTION_TYPES.CLEAR_LOCK_INTERVAL,
});

export const setPlazaEditorLockTimeout = (lockTimeout) => ({
  type: ACTION_TYPES.SET_LOCK_TIMEOUT,
  payload: lockTimeout,
});

export const clearPlazaEditorLockTimeout = () => ({
  type: ACTION_TYPES.CLEAR_LOCK_TIMEOUT,
});

export const setPlazaEditorConfirmationTimeout = (confirmationTimeout) => ({
  type: ACTION_TYPES.SET_CONFIRMATION_TIMEOUT,
  payload: confirmationTimeout,
});

export const clearPlazaEditorConfirmationTimeout = () => ({
  type: ACTION_TYPES.CLEAR_CONFIRMATION_TIMEOUT,
});

export const updateLockStatues = (editable, username) => ({
  type: ACTION_TYPES.UPDATE_LOCK_STATUS,
  editable,
  username,
});

export const resetPlazaEditor =
  (plazaId, shouldUpdate = false, shouldReset = true) =>
  (dispatch) => {
    dispatch(clearPlazaEditorLockInterval());
    dispatch(clearPlazaEditorLockTimeout());
    dispatch(clearPlazaEditorConfirmationTimeout());

    if (shouldReset) {
      dispatch(resetToInitialState());
    }

    if (shouldUpdate) {
      dispatch(loadMonitoringPlaza(plazaId));
    }
  };

export const getLockStatus = (plazaId) => (dispatch) => {
  PlazaResource.getLockStatus({ plazaId }).then(({ data }) => {
    const { lockUserName = null, editable } = data;

    dispatch(updateLockStatues(editable, lockUserName));
  });
};
export const unlockPlazaEditor = (plazaId, locked) => (dispatch) => {
  if (!locked) {
    PlazaResource.editingUnlock({ plazaId }).catch((err) => {
      if (get(err, 'response.status', null) === 423) {
        dispatch(setEditorEditable(false));
        dispatch(resetPlazaEditor(plazaId, true));
      }
    });
  }
  dispatch(resetPlazaEditor(plazaId, false, false));
};

export const lockPlazaEditor = (plazaId, locked, isSentLock) => (dispatch) => {
  const lockTimeout = setTimeout(
    () => {
      const confirmationTimeout = setTimeout(() => {
        dispatch(unlockPlazaEditor(plazaId, locked));
        dispatch(resetPlazaEditor(plazaId));
      }, 60 * 1000);

      dispatch(setPlazaEditorConfirmationTimeout(confirmationTimeout));
    },
    5 * 60 * 1000,
  );

  dispatch(clearPlazaEditorLockTimeout());
  dispatch(clearPlazaEditorConfirmationTimeout());
  dispatch(setPlazaEditorLockTimeout(lockTimeout));

  if (!locked && !isSentLock) {
    PlazaResource.editingLock({ plazaId })
      .then(() => {
        const lockInterval = setInterval(
          () => {
            PlazaResource.editingLock({ plazaId });
          },
          5 * 60 * 1000,
        );

        dispatch(setPlazaEditorLockInterval(lockInterval));
      })
      .catch((err) => {
        if (get(err, 'response.status', null) === 423) {
          dispatch(
            setMessage({
              message:
                'The Plaza parameters are being modified by another user. Please leave the Editor and check it later.',
              buttonText: 'Ok',
            }),
          );
          const { lockUserName = null } = get(err, 'response.data', {});

          dispatch(updateLockStatues(false, lockUserName));
          dispatch(resetPlazaEditor(plazaId, true));
        }
      });
  }
};
